import React, { useEffect, useState } from 'react';
import { X, Sparkles, MapPin, MessageCircle } from 'lucide-react';

const STORAGE_KEY = 'sgcabs_promo_outstation_seen_v1';

export const PromoBanner = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Show only once — persist dismissal in localStorage
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        // Tiny delay so it appears after first paint, not on top of LCP
        const t = setTimeout(() => setOpen(true), 800);
        return () => clearTimeout(t);
      }
    } catch (e) {
      // localStorage blocked (private mode etc.) — show anyway
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  const handleBookNow = () => {
    dismiss();
    const el = document.getElementById('booking');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleWhatsApp = () => {
    dismiss();
    const msg = encodeURIComponent(
      "Hi SG Cabs! I'd like to book an outstation trip and use the *SGCABS10* promo (10–15% off). Please share details."
    );
    window.open(`https://wa.me/917507867648?text=${msg}`, '_blank', 'noopener,noreferrer');
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="promo-banner-title"
      data-testid="promo-banner"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-black via-gray-900 to-black border border-[#d4af37]/30 animate-pop-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative gold ring */}
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#d4af37]/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-[#d4af37]/10 blur-3xl pointer-events-none" />

        {/* Close */}
        <button
          onClick={dismiss}
          aria-label="Close promo"
          data-testid="promo-banner-close"
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative px-7 pt-9 pb-7 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#d4af37]" />
            <span className="text-[11px] font-semibold tracking-widest text-[#d4af37] uppercase">
              Limited Time
            </span>
          </div>

          {/* Headline */}
          <h2
            id="promo-banner-title"
            className="text-3xl sm:text-4xl font-extrabold text-white leading-tight"
          >
            Flat <span className="text-[#d4af37]">10–15%</span>
            <br />
            <span className="text-white">Off Outstation Trips</span>
          </h2>

          <p className="text-gray-300 text-sm mt-3 leading-relaxed">
            Plan your next outstation getaway with SG Cabs and save on every
            kilometre. Comfortable rides, experienced drivers, transparent pricing.
          </p>

          {/* Outstation perks chips */}
          <div className="flex flex-wrap justify-center gap-1.5 mt-4">
            {['All Outstation Routes', 'Sedan & SUV', 'Pan-India'].map((r) => (
              <span
                key={r}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-gray-200"
              >
                <MapPin className="w-3 h-3 text-[#d4af37]" />
                {r}
              </span>
            ))}
          </div>

          {/* CTAs */}
          <div className="mt-6 flex flex-col gap-2">
            <button
              onClick={handleBookNow}
              data-testid="promo-banner-cta"
              className="w-full py-3 rounded-lg font-semibold text-base bg-[#d4af37] hover:bg-[#c9a35c] text-black shadow-lg shadow-[#d4af37]/20 transition-all hover:scale-[1.02]"
            >
              Book My Outstation Trip
            </button>
            <button
              type="button"
              onClick={handleWhatsApp}
              data-testid="promo-banner-whatsapp"
              className="w-full py-2.5 rounded-lg font-medium text-sm bg-green-600 hover:bg-green-500 text-white transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Now
            </button>
          </div>

          {/* Code line */}
          <p className="mt-4 text-[11px] text-gray-400 leading-relaxed">
            Use code <span className="font-bold text-[#d4af37] tracking-wider">SGCABS10</span> while booking.
            <br />
            <span className="opacity-75">*T&amp;C applicable. Valid on outstation trips only. One-time use per customer.</span>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes promoFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes promoPopIn {
          from { opacity: 0; transform: scale(0.92) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fade-in { animation: promoFadeIn 0.25s ease-out both; }
        .animate-pop-in  { animation: promoPopIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both; }
      `}</style>
    </div>
  );
};
